import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { apiService } from '../services/api';
import { 
  Plus, Search, Edit2, Trash2, Network, Activity, AlertTriangle,
  CheckCircle, Clock, Thermometer, Zap, Wifi, Settings, Eye, EyeOff,
  Building2, MapPin, User, Calendar, BarChart3, Cpu, MemoryStick, Printer, Copy, Archive, Database, X, Download, FileText, ChevronLeft, ChevronRight, Shield
} from 'lucide-react';
import { networkSwitches, companies, locations, allUsers } from '../data/mockData';
import { NetworkSwitch } from '../types';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
// @ts-ignore
import 'jspdf-autotable';
import Modal from './Modal';

const NetworkSwitchesPage: React.FC = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showModelListModal, setShowModelListModal] = useState(false);
  const [editingSwitch, setEditingSwitch] = useState<NetworkSwitch | null>(null);
  const [editingModel, setEditingModel] = useState<any | null>(null);
  const [selectedSwitch, setSelectedSwitch] = useState<NetworkSwitch | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('all');
  const [filterHealth, setFilterHealth] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [switchesData, setSwitchesData] = useState<NetworkSwitch[]>([]);
  const [switchModelsData, setSwitchModelsData] = useState<any[]>([]);
  const [cabinetsData, setCabinetsData] = useState<any[]>([]);
  const [companiesData, setCompaniesData] = useState<any[]>([]);
  const [locationsData, setLocationsData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  
  const [switchData, setSwitchData] = useState({
    switch_name: '',
    alias: '',
    brand: '',
    model: '',
    serial_number: '',
    ip_address: '',
    mac_address: '',
    inventory_number: '',
    port_count: 24,
    port_type: 'gigabit' as NetworkSwitch['port_type'],
    poe_support: false,
    poe_budget_watts: 0,
    firmware_version: '',
    location_id: 0,
    company_id: 0,
    cabinet_id: 0,
    rack_position: '',
    management_vlan: 1,
    uplink_ports: '',
    status: 'active' as NetworkSwitch['status'],
    purchase_date: '',
    warranty_end_date: '',
    purchase_price: 0,
    currency: 'TRY' as 'TRY' | 'USD' | 'EUR',
    assigned_admin_id: 0,
    next_maintenance: '',
    notes: '',

    admin_username: '',
    admin_password: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [lldpNeighbors, setLldpNeighbors] = useState([
    { neighbor_switch_id: 0, local_port: '', remote_port: '' }
  ]);

  const [modelData, setModelData] = useState({
    brand: '',
    model: '',
    port_count: 24,
    port_type: 'gigabit',
    poe_support: false,
    poe_budget_watts: 0,
    port_speeds: '',
    sfp_ports: 0,
    technical_specs: '',
    description: ''
  });
  const [modelImageFile, setModelImageFile] = useState<File | null>(null);

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [switchesResponse, modelsResponse, cabinetsResponse, companiesResponse, locationsResponse, vlansResponse] = await Promise.all([
        apiService.getNetworkSwitches(),
        apiService.getSwitchModels(),
        apiService.getCabinets(),
        apiService.getCompanies(),
        apiService.getLocations(),
        apiService.getVLANs()
      ]);
      setSwitchesData(Array.isArray(switchesResponse) ? switchesResponse : []);
      setSwitchModelsData(Array.isArray(modelsResponse) ? modelsResponse : []);
      setCabinetsData(Array.isArray(cabinetsResponse) ? cabinetsResponse : []);
      setCompaniesData(Array.isArray(companiesResponse) ? companiesResponse : []);
      setLocationsData(Array.isArray(locationsResponse) ? locationsResponse : []);
      setVlansData(Array.isArray(vlansResponse) ? vlansResponse : []);
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(t('notif.filter.error'), t('network.errLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'active', label: t('network.statusActive'), color: 'bg-green-100 text-green-800' },
    { value: 'inactive', label: t('network.statusInactive'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' },
    { value: 'maintenance', label: t('network.statusMaintenance'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'faulty', label: t('network.statusFaulty'), color: 'bg-red-100 text-red-800' }
  ];

  const healthOptions = [
    { value: 'healthy', label: t('network.healthHealthy'), color: 'bg-green-100 text-green-800' },
    { value: 'warning', label: t('network.healthWarning'), color: 'bg-yellow-100 text-yellow-800' },
    { value: 'critical', label: t('network.healthCritical'), color: 'bg-red-100 text-red-800' },
    { value: 'unknown', label: t('network.healthUnknown'), color: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200' }
  ];

  const portTypeOptions = [
    { value: 'gigabit', label: 'Gigabit Ethernet' },
    { value: 'fast_ethernet', label: 'Fast Ethernet' },
    { value: '10g', label: '10 Gigabit' },
    { value: 'sfp', label: 'SFP/SFP+' }
  ];

  const currentSelectedModel = switchModelsData.find(
    m => m.brand === switchData.brand && m.model === switchData.model
  );

  const filteredSwitches = switchesData.filter(sw => {
    const matchesSearch = String(sw.switch_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(sw.alias || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(sw.model || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                         String(sw.ip_address || '').includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || sw.status === filterStatus;
    const matchesHealth = filterHealth === 'all' || sw.health_status === filterHealth;

    return matchesSearch && matchesStatus && matchesHealth;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredSwitches.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSwitches = filteredSwitches.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterHealth]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const exportToExcel = () => {
    try {
      const locale = language === 'en' ? 'en-US' : 'tr-TR';
      const exportData = filteredSwitches.map(sw => ({
        [language === 'en' ? 'Switch Name' : 'Switch Adı']: sw.switch_name,
        [language === 'en' ? 'Alias' : 'Takma Ad']: sw.alias || '',
        [language === 'en' ? 'Brand' : 'Marka']: sw.brand,
        'Model': sw.model,
        [language === 'en' ? 'Serial Number' : 'Seri Numarası']: sw.serial_number || '',
        [language === 'en' ? 'IP Address' : 'IP Adresi']: sw.ip_address,
        [language === 'en' ? 'MAC Address' : 'MAC Adresi']: sw.mac_address || '',
        [language === 'en' ? 'Inventory No' : 'Envanter No']: sw.inventory_number || '',
        [language === 'en' ? 'Port Count' : 'Port Sayısı']: sw.port_count,
        [language === 'en' ? 'Active Ports' : 'Aktif Portlar']: sw.active_ports || 0,
        [language === 'en' ? 'Port Type' : 'Port Tipi']: portTypeOptions.find(opt => opt.value === sw.port_type)?.label || sw.port_type,
        [language === 'en' ? 'PoE Support' : 'PoE Desteği']: sw.poe_support ? t('network.yes') : t('network.no'),
        [language === 'en' ? 'PoE Budget (Watt)' : 'PoE Bütçe (Watt)']: sw.poe_budget_watts || 0,
        [language === 'en' ? 'Firmware Version' : 'Firmware Versiyonu']: sw.firmware_version || '',
        [language === 'en' ? 'Location' : 'Lokasyon']: sw.location?.name || '',
        [language === 'en' ? 'Company' : 'Şirket']: sw.company?.name || '',
        [language === 'en' ? 'Cabinet' : 'Kabin']: sw.cabinet?.name || '',
        [language === 'en' ? 'Rack Position' : 'Rack Pozisyonu']: sw.rack_position || '',
        [language === 'en' ? 'Management VLAN' : 'Yönetim VLAN']: sw.management_vlan || '',
        [language === 'en' ? 'Uplink Ports' : 'Uplink Portları']: sw.uplink_ports || '',
        [language === 'en' ? 'Status' : 'Durum']: statusOptions.find(opt => opt.value === sw.status)?.label || sw.status,
        [language === 'en' ? 'Health Status' : 'Sağlık Durumu']: healthOptions.find(opt => opt.value === sw.health_status)?.label || t('network.healthUnknown'),
        [language === 'en' ? 'Purchase Date' : 'Satın Alma Tarihi']: sw.purchase_date ? new Date(sw.purchase_date).toLocaleDateString(locale) : '',
        [language === 'en' ? 'Warranty End Date' : 'Garanti Bitiş Tarihi']: sw.warranty_end_date ? new Date(sw.warranty_end_date).toLocaleDateString(locale) : '',
        [language === 'en' ? 'Purchase Price' : 'Satın Alma Fiyatı']: sw.purchase_price ? `${sw.purchase_price} ${sw.currency || 'TRY'}` : '',
        [language === 'en' ? 'Next Maintenance' : 'Sonraki Bakım']: sw.next_maintenance ? new Date(sw.next_maintenance).toLocaleDateString(locale) : '',
        [language === 'en' ? 'Notes' : 'Notlar']: sw.notes || ''
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, language === 'en' ? 'Network Switches' : 'Network Switchler');
      
      // Set column widths
      const colWidths = [
        { wch: 20 }, // Switch Adı
        { wch: 15 }, // Marka
        { wch: 15 }, // Model
        { wch: 18 }, // Seri Numarası
        { wch: 15 }, // IP Adresi
        { wch: 18 }, // MAC Adresi
        { wch: 15 }, // Envanter No
        { wch: 12 }, // Port Sayısı
        { wch: 12 }, // Aktif Portlar
        { wch: 15 }, // Port Tipi
        { wch: 12 }, // PoE Desteği
        { wch: 15 }, // PoE Bütçe
        { wch: 18 }, // Firmware
        { wch: 20 }, // Lokasyon
        { wch: 20 }, // Şirket
        { wch: 20 }, // Kabin
        { wch: 15 }, // Rack Pozisyonu
        { wch: 15 }, // Yönetim VLAN
        { wch: 15 }, // Uplink Portları
        { wch: 12 }, // Durum
        { wch: 15 }, // Sağlık Durumu
        { wch: 15 }, // Satın Alma Tarihi
        { wch: 18 }, // Garanti Bitiş
        { wch: 18 }, // Satın Alma Fiyatı
        { wch: 15 }, // Sonraki Bakım
        { wch: 30 }  // Notlar
      ];
      ws['!cols'] = colWidths;

      const filename = `network-switchler-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);
      
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', language === 'en' ? `${filteredSwitches.length} switches exported to Excel` : `${filteredSwitches.length} switch başarıyla Excel'e aktarıldı`);
    } catch (error: any) {
      console.error('Excel export error:', error);
      showGlobalNotification.error(t('notif.filter.error'), language === 'en' ? 'Error creating Excel file' : 'Excel dosyası oluşturulurken hata oluştu');
    }
  };

  const exportToPDF = () => {
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // landscape orientation
      const pdfLocale = language === 'en' ? 'en-US' : 'tr-TR';

      // Add title
      pdf.setFontSize(16);
      pdf.text(language === 'en' ? 'Network Switches List' : 'Network Switchler Listesi', 14, 15);

      // Add date
      pdf.setFontSize(10);
      pdf.text(`${language === 'en' ? 'Report Date' : 'Rapor Tarihi'}: ${new Date().toLocaleDateString(pdfLocale)}`, 14, 22);
      pdf.text(`${language === 'en' ? 'Total Switches' : 'Toplam Switch'}: ${filteredSwitches.length}`, 200, 22);

      // Prepare table data
      const tableData = filteredSwitches.map(sw => [
        sw.switch_name,
        sw.alias || '',
        `${sw.brand} ${sw.model}`,
        sw.ip_address,
        sw.port_count.toString(),
        (sw.active_ports || 0).toString(),
        sw.poe_support ? t('network.yes') : t('network.no'),
        sw.location?.name || '',
        sw.company?.name || '',
        statusOptions.find(opt => opt.value === sw.status)?.label || sw.status
      ]);

      // Add table using autoTable plugin
      (pdf as any).autoTable({
        head: [[language === 'en' ? 'Switch Name' : 'Switch Adı', language === 'en' ? 'Alias' : 'Takma Ad', language === 'en' ? 'Brand/Model' : 'Marka/Model', language === 'en' ? 'IP Address' : 'IP Adresi', language === 'en' ? 'Port' : 'Port', language === 'en' ? 'Active' : 'Aktif', 'PoE', language === 'en' ? 'Location' : 'Lokasyon', language === 'en' ? 'Company' : 'Şirket', language === 'en' ? 'Status' : 'Durum']],
        body: tableData,
        startY: 28,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        columnStyles: {
          0: { cellWidth: 28 },
          1: { cellWidth: 25 },
          2: { cellWidth: 30 },
          3: { cellWidth: 22 },
          4: { cellWidth: 12 },
          5: { cellWidth: 12 },
          6: { cellWidth: 12 },
          7: { cellWidth: 22 },
          8: { cellWidth: 22 },
          9: { cellWidth: 18 }
        },
        margin: { top: 28, left: 14, right: 14 }
      });

      // Add summary on new page if needed
      if (filteredSwitches.length > 0) {
        const activeCount = filteredSwitches.filter(s => s.status === 'active').length;
        const poeCount = filteredSwitches.filter(s => s.poe_support).length;
        const totalPorts = filteredSwitches.reduce((sum, s) => sum + s.port_count, 0);
        
        pdf.addPage();
        pdf.setFontSize(14);
        pdf.text(language === 'en' ? 'Summary' : 'Özet Bilgiler', 14, 15);
        pdf.setFontSize(10);

        let yPos = 25;
        pdf.text(`${language === 'en' ? 'Total Switches' : 'Toplam Switch'}: ${filteredSwitches.length}`, 14, yPos);
        yPos += 8;
        pdf.text(`${language === 'en' ? 'Active Switches' : 'Aktif Switch'}: ${activeCount}`, 14, yPos);
        yPos += 8;
        pdf.text(`${language === 'en' ? 'PoE Supported' : 'PoE Destekli'}: ${poeCount}`, 14, yPos);
        yPos += 8;
        pdf.text(`${language === 'en' ? 'Total Ports' : 'Toplam Port'}: ${totalPorts}`, 14, yPos);
      }

      const filename = `network-switchler-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);

      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', language === 'en' ? `${filteredSwitches.length} switches exported to PDF` : `${filteredSwitches.length} switch başarıyla PDF'e aktarıldı`);
    } catch (error: any) {
      console.error('PDF export error:', error);
      showGlobalNotification.error(t('notif.filter.error'), language === 'en' ? 'Error creating PDF file' : 'PDF dosyası oluşturulurken hata oluştu');
    }
  };

  const handleEdit = (sw: NetworkSwitch) => {
    setEditingSwitch(sw);
    setSwitchData({
      switch_name: sw.switch_name,
      brand: sw.brand,
      model: sw.model,
      serial_number: sw.serial_number,
      ip_address: sw.ip_address,
      mac_address: sw.mac_address,
      inventory_number: sw.inventory_number || '',
      port_count: sw.port_count,
      port_type: sw.port_type,
      poe_support: sw.poe_support,
      poe_budget_watts: sw.poe_budget_watts || 0,
      firmware_version: sw.firmware_version,
      location_id: sw.location?.id || sw.location_id || 0,
      company_id: sw.company?.id || sw.company_id || 0,
      cabinet_id: sw.cabinet_id || 0,
      rack_position: sw.rack_position || '',
      management_vlan: sw.management_vlan || 1,
      uplink_ports: sw.uplink_ports || '',
      status: sw.status,
      purchase_date: sw.purchase_date ? sw.purchase_date.split('T')[0] : '',
      warranty_end_date: sw.warranty_end_date ? sw.warranty_end_date.split('T')[0] : '',
      purchase_price: sw.purchase_price || 0,
      currency: sw.currency || 'TRY',
      assigned_admin_id: sw.assigned_admin_id || 0,
      next_maintenance: sw.next_maintenance ? sw.next_maintenance.split('T')[0] : '',
      notes: sw.notes || '',
      admin_username: sw.admin_username || '',
      admin_password: sw.admin_password || ''
    });
    
    // Load LLDP neighbors
    if (sw.lldp_neighbors && sw.lldp_neighbors.length > 0) {
      setLldpNeighbors(sw.lldp_neighbors.map((neighbor: any) => ({
        neighbor_switch_id: neighbor.neighbor_switch_id,
        local_port: neighbor.local_port,
        remote_port: neighbor.remote_port
      })));
    } else {
      setLldpNeighbors([{ neighbor_switch_id: 0, local_port: '', remote_port: '' }]);
    }
    setImageFile(null);
    setShowPassword(false);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingSwitch(null);
    setSwitchData({
      switch_name: '',
      brand: '',
      model: '',
      serial_number: '',
      ip_address: '',
      mac_address: '',
      inventory_number: '',
      port_count: 24,
      port_type: 'gigabit',
      poe_support: false,
      poe_budget_watts: 0,
      firmware_version: '',
      location_id: 0,
      company_id: 0,
      cabinet_id: 0,
      rack_position: '',
      management_vlan: 1,
      uplink_ports: '',
      status: 'active',
      purchase_date: '',
      warranty_end_date: '',
      purchase_price: 0,
      currency: 'TRY',
      assigned_admin_id: 0,
      next_maintenance: '',
      notes: '',
      admin_username: '',
      admin_password: ''
    });
    setLldpNeighbors([
      { neighbor_switch_id: 0, local_port: '', remote_port: '' }
    ]);
    setImageFile(null);
    setShowModal(true);
  };

  const handleCopy = async (sw: NetworkSwitch) => {
    setEditingSwitch(null);
    
    // Aynı kabindeki mevcut switch'leri kontrol et
    const existingSwitches = switchesData.filter(existingSw => 
      existingSw.cabinet_id === sw.cabinet_id && existingSw.id !== sw.id
    );
    
    // Yeni switch adı oluştur
    let newSwitchName = sw.switch_name;
    if (sw.cabinet_id && cabinetsData.length > 0) {
      const cabinet = cabinetsData.find(c => c.id === sw.cabinet_id);
      if (cabinet) {
        let switchNumber = 1;
        do {
          const paddedNumber = switchNumber.toString().padStart(2, '0');
          newSwitchName = `${cabinet.cabinet_name}-SW${paddedNumber}`;
          switchNumber++;
        } while (existingSwitches.some(existingSw => existingSw.switch_name === newSwitchName));
      }
    } else {
      newSwitchName = language === 'en' ? `${sw.switch_name} - Copy` : `${sw.switch_name} - Kopya`;
    }
    
    setSwitchData({
      switch_name: newSwitchName,
      brand: sw.brand,
      model: sw.model,
      serial_number: '', // Seri numarası boş bırak
      ip_address: '', // IP adresi boş bırak
      mac_address: '', // MAC adresi boş bırak
      inventory_number: '',
      port_count: sw.port_count,
      port_type: sw.port_type,
      poe_support: sw.poe_support,
      poe_budget_watts: sw.poe_budget_watts || 0,
      firmware_version: sw.firmware_version,
      location_id: sw.location?.id || sw.location_id || 0,
      company_id: sw.company?.id || sw.company_id || 0,
      cabinet_id: sw.cabinet_id || 0,
      rack_position: sw.rack_position || '',
      management_vlan: sw.management_vlan || 1,
      uplink_ports: sw.uplink_ports || '',
      status: 'active',
      purchase_date: '',
      warranty_end_date: '',
      purchase_price: sw.purchase_price || 0,
      currency: sw.currency || 'TRY',
      assigned_admin_id: sw.assigned_admin_id || 0,
      next_maintenance: '',
      notes: sw.notes || '',
      admin_username: sw.admin_username || '',
      admin_password: sw.admin_password || ''
    });
    
    // LLDP komşularını kopyalama
    setLldpNeighbors([
      { neighbor_switch_id: 0, local_port: '', remote_port: '' }
    ]);
    
    setImageFile(null);
    setShowModal(true);
    showGlobalNotification.info(language === 'en' ? 'Info' : 'Bilgi', t('network.switchCopied'));
  };



  const handleSaveAsync = async () => {
    // Zorunlu alanlar kontrolü
    if (!switchData.switch_name || !switchData.ip_address || !switchData.serial_number) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errRequiredFields'));
      return;
    }
    
    // Switch adı benzersizlik kontrolü (aynı kabin içinde)
    if (switchData.cabinet_id) {
      const duplicateName = switchesData.find(sw => 
        sw.cabinet_id === switchData.cabinet_id && 
        sw.switch_name === switchData.switch_name &&
        (!editingSwitch || sw.id !== editingSwitch.id)
      );
      
      if (duplicateName) {
        showGlobalNotification.error(t('notif.filter.error'), language === 'en' ? `A switch named "${switchData.switch_name}" already exists in this cabinet` : `Bu kabinde "${switchData.switch_name}" adlı switch zaten mevcut`);
        return;
      }
    }
    
    // IP adresi ve seri numarası benzersizlik kontrolü
    // Düzenlenen switch'in orijinal IP ve seri numarasını hariç tutmalı
    const existingSwitch = switchesData.find(sw => {
      // Eğer düzenleme yapıyorsa, düzenlenen switch'i hariç tut
      if (editingSwitch && sw.id === editingSwitch.id) {
        return false;
      }

      // IP adresi kontrolü - IP değiştirilmişse yeni IP'nin başka bir switch'te olup olmadığını kontrol et
      if (sw.ip_address === switchData.ip_address &&
          (!editingSwitch || editingSwitch.ip_address !== switchData.ip_address)) {
        return true;
      }

      // Seri numarası kontrolü - Seri numarası değiştirilmişse yeni seri numaranın başka bir switch'te olup olmadığını kontrol et
      if (sw.serial_number === switchData.serial_number &&
          (!editingSwitch || editingSwitch.serial_number !== switchData.serial_number)) {
        return true;
      }

      return false;
    });

    if (existingSwitch) {
      if (existingSwitch.ip_address === switchData.ip_address) {
        showGlobalNotification.error(t('notif.filter.error'), t('network.errDuplicateIP'));
        return;
      }
      if (existingSwitch.serial_number === switchData.serial_number) {
        showGlobalNotification.error(t('notif.filter.error'), t('network.errDuplicateSerial'));
        return;
      }
    }
    
    try {
      const validNeighbors = lldpNeighbors.filter(n => n.neighbor_switch_id && n.local_port && n.remote_port);
      
      const formData = new FormData();
      
      // Zorunlu alanları önce ekle
      formData.append('switch_name', switchData.switch_name);
      formData.append('brand', switchData.brand);
      formData.append('model', switchData.model);
      formData.append('serial_number', switchData.serial_number);
      formData.append('ip_address', switchData.ip_address);
      formData.append('mac_address', switchData.mac_address);
      formData.append('firmware_version', switchData.firmware_version);
      formData.append('location_id', switchData.location_id.toString());
      formData.append('company_id', switchData.company_id.toString());
      formData.append('port_count', switchData.port_count.toString());
      formData.append('port_type', switchData.port_type);
      formData.append('status', switchData.status);
      formData.append('management_vlan', switchData.management_vlan.toString());
      formData.append('currency', switchData.currency);
      
      // Opsiyonel alanları ekle
      Object.keys(switchData).forEach(key => {
        if (!['switch_name', 'brand', 'model', 'serial_number', 'ip_address', 'mac_address', 'firmware_version', 'location_id', 'company_id', 'port_count', 'port_type', 'status', 'management_vlan', 'currency'].includes(key)) {
          const value = switchData[key as keyof typeof switchData];
          if (value !== null && value !== undefined && value !== '') {
            formData.append(key, value.toString());
          }
        }
      });
      
      // Boolean değerleri düzelt
      formData.set('poe_support', Boolean(switchData.poe_support).toString());
      
      // Özel durumlar
      if (switchData.assigned_admin_id > 0) {
        formData.set('assigned_admin_id', switchData.assigned_admin_id.toString());
      }
      
      
      // LLDP komşularını ekle
      formData.append('lldp_neighbors', JSON.stringify(validNeighbors));
      
      // Görsel dosyasını ekle
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      if (editingSwitch) {
        await apiService.updateNetworkSwitch(editingSwitch.id, formData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successSwitchUpdated'));
      } else {
        await apiService.createNetworkSwitch(formData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successSwitchCreated'));
      }

      await loadData();
      setShowModal(false);
    } catch (error: any) {
      console.error('Error saving network switch:', error);
      showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errSavingSwitch'));
    }
  };

  const handleDelete = async (id: number) => {
    const switchToDelete = switchesData.find(s => s.id === id);
    if (!switchToDelete) return;
    
    if (confirm(t('network.confirmDeleteSwitch'))) {
      try {
        await apiService.deleteNetworkSwitch(id);
        await loadData();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successSwitchDeleted'));
      } catch (error: any) {
        console.error('Error deleting network switch:', error);
        showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errDeletingSwitch'));
      }
    }
  };

  const showSwitchDetail = (sw: NetworkSwitch) => {
    setSelectedSwitch(sw);
    setShowDetailModal(true);
  };

  const getStatusColor = (status: string) => {
    return statusOptions.find(s => s.value === status)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getHealthColor = (health: string) => {
    return healthOptions.find(h => h.value === health)?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200';
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'TRY': return '₺';
      default: return '₺';
    }
  };

  const showPortManagement = async (sw: NetworkSwitch) => {
    try {
      setSelectedSwitchForPorts(sw);
      const ports = await apiService.getSwitchPorts(sw.id);
      setSwitchPorts(ports);
      setShowPortModal(true);
    } catch (error: any) {
      console.error('Error loading ports:', error);
      showGlobalNotification.error(t('notif.filter.error'), t('network.errLoadPorts'));
    }
  };

  const handlePortEdit = (port: any) => {
    setEditingPort(port);
  };

  const handlePortSave = async (portId: number, portData: any) => {
    try {
      await apiService.updateSwitchPort(portId, portData);
      const updatedPorts = await apiService.getSwitchPorts(selectedSwitchForPorts!.id);
      setSwitchPorts(updatedPorts);
      setEditingPort(null);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successPortUpdated'));
    } catch (error: any) {
      console.error('Error updating port:', error);
      showGlobalNotification.error(t('notif.filter.error'), t('network.errUpdatePort'));
    }
  };

  const handleVlanSave = async () => {
    if (!vlanData.vlan_id || !vlanData.vlan_name) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errVlanRequired'));
      return;
    }
    
    try {
      if (editingVlan) {
        await apiService.updateVLAN(editingVlan.id, vlanData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successVlanUpdated'));
      } else {
        await apiService.createVLAN(vlanData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successVlanCreated'));
      }

      await loadData();
      setShowVlanModal(false);
      setEditingVlan(null);
      setVlanData({ vlan_id: '', vlan_name: '', description: '' });
    } catch (error: any) {
      console.error('Error saving VLAN:', error);
      showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errSavingVlan'));
    }
  };

  const handleVlanDelete = async (id: number) => {
    const vlanToDelete = vlansData.find(v => v.id === id);
    if (!vlanToDelete) return;
    
    if (confirm(language === 'en' ? `Are you sure you want to delete VLAN "${vlanToDelete.vlan_name}"?` : `"${vlanToDelete.vlan_name}" VLAN'ını silmek istediğinizden emin misiniz?`)) {
      try {
        await apiService.deleteVLAN(id);
        await loadData();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', language === 'en' ? 'VLAN deleted' : 'VLAN silindi');
      } catch (error: any) {
        console.error('Error deleting VLAN:', error);
        showGlobalNotification.error(t('notif.filter.error'), language === 'en' ? 'Error deleting VLAN' : 'VLAN silinirken hata oluştu');
      }
    }
  };

  const calculateNetworkInfo = (cidr: string) => {
    if (!cidr || !cidr.includes('/')) return;
    
    const [networkAddr, prefixLength] = cidr.split('/');
    const prefix = parseInt(prefixLength);
    
    if (prefix < 0 || prefix > 32) return;
    
    // Calculate subnet mask
    const mask = (0xffffffff << (32 - prefix)) >>> 0;
    const subnetMask = [
      (mask >>> 24) & 0xff,
      (mask >>> 16) & 0xff,
      (mask >>> 8) & 0xff,
      mask & 0xff
    ].join('.');
    
    // Calculate network and broadcast addresses
    const networkParts = networkAddr.split('.').map(Number);
    const networkInt = (networkParts[0] << 24) + (networkParts[1] << 16) + (networkParts[2] << 8) + networkParts[3];
    const hostBits = 32 - prefix;
    const numHosts = Math.pow(2, hostBits);
    const broadcastInt = networkInt + numHosts - 1;
    
    // First and last usable IPs
    const firstUsableInt = networkInt + 1;
    const lastUsableInt = broadcastInt - 1;
    
    const intToIp = (int: number) => [
      (int >>> 24) & 0xff,
      (int >>> 16) & 0xff,
      (int >>> 8) & 0xff,
      int & 0xff
    ].join('.');
    
    const firstUsable = intToIp(firstUsableInt);
    const lastUsable = intToIp(lastUsableInt);
    const ipRange = `${firstUsable} - ${lastUsable}`;
    
    // Calculate gateway (first usable IP)
    const gateway = [...networkParts];
    gateway[3] += 1;
    
    setVlanData(prev => ({
      ...prev,
      subnet_mask: subnetMask,
      gateway: gateway.join('.'),
      ip_range: ipRange
    }));
  };

  // Maintenance modal states
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [selectedSwitchForMaintenance, setSelectedSwitchForMaintenance] = useState<NetworkSwitch | null>(null);
  const [maintenanceData, setMaintenanceData] = useState({
    maintenance_type: 'cleaning',
    maintenance_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [selectedSwitchForLabel, setSelectedSwitchForLabel] = useState<NetworkSwitch | null>(null);
  const [labelSettings, setLabelSettings] = useState({
    width: 100,
    height: 50,
    orientation: 'horizontal',
    margin_top: 2,
    margin_right: 2,
    margin_bottom: 2,
    margin_left: 2
  });

  // Backup modal states
  const [showBackupModal, setShowBackupModal] = useState(false);
  const [backupData, setBackupData] = useState({
    backup_type: 'text',
    config_text: '',
    description: ''
  });
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [backupHistory, setBackupHistory] = useState<any[]>([]);
  const [configAnalysis, setConfigAnalysis] = useState<any>(null);
  const [analyzingConfig, setAnalyzingConfig] = useState(false);
  const [analyzeTimer, setAnalyzeTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const [showEmptyPortsModal, setShowEmptyPortsModal] = useState(false);
  const [emptyPorts, setEmptyPorts] = useState<any[]>([]);

  // Port management states
  const [showPortModal, setShowPortModal] = useState(false);
  const [selectedSwitchForPorts, setSelectedSwitchForPorts] = useState<NetworkSwitch | null>(null);
  const [switchPorts, setSwitchPorts] = useState<any[]>([]);
  const [editingPort, setEditingPort] = useState<any | null>(null);
  const [vlansData, setVlansData] = useState<any[]>([]);
  const [showVlanModal, setShowVlanModal] = useState(false);
  const [editingVlan, setEditingVlan] = useState<any | null>(null);
  const [vlanData, setVlanData] = useState({
    vlan_id: '',
    vlan_name: '',
    description: '',
    network_address: '',
    subnet_mask: '',
    gateway: '',
    ip_range: '',
    location_id: 0
  });
  const [showVlanDetailModal, setShowVlanDetailModal] = useState(false);
  const [selectedVlanForDetail, setSelectedVlanForDetail] = useState<any | null>(null);
  const [vlanSearchTerm, setVlanSearchTerm] = useState('');
  const [modelSearchTerm, setModelSearchTerm] = useState('');

  // Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string>('');

  const saveMaintenance = async () => {
    if (!selectedSwitchForMaintenance || !maintenanceData.maintenance_date) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errMaintenanceRequired'));
      return;
    }

    try {
      await apiService.addSwitchMaintenance(selectedSwitchForMaintenance.id, maintenanceData);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successMaintenanceSaved'));
      setShowMaintenanceModal(false);
      setMaintenanceData({
        maintenance_type: 'cleaning',
        maintenance_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      await loadData();
    } catch (error: any) {
      console.error('Error saving maintenance:', error);
      showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errSavingMaintenance'));
    }
  };

  const showEmptyPorts = async (sw: NetworkSwitch) => {
    try {
      const ports = await apiService.getSwitchPorts(sw.id);
      const emptyPortsList = ports.filter((port: any) => !port.is_active || port.is_active === 0);
      setEmptyPorts(emptyPortsList);
      setShowEmptyPortsModal(true);
    } catch (error: any) {
      console.error('Error loading empty ports:', error);
      showGlobalNotification.error(t('notif.filter.error'), t('network.errLoadEmptyPorts'));
    }
  };

  const loadBackupHistory = async (switchId: number) => {
    try {
      const backups = await apiService.getSwitchBackups(switchId);
      setBackupHistory(backups);
    } catch (error: any) {
      console.error('Error loading backup history:', error);
      setBackupHistory([]);
    }
  };

  const triggerConfigAnalysis = (text: string) => {
    if (analyzeTimer) clearTimeout(analyzeTimer);
    if (!text.trim()) { setConfigAnalysis(null); return; }
    const timer = setTimeout(async () => {
      setAnalyzingConfig(true);
      try {
        const result = await apiService.analyzeNetworkSwitchConfig(text);
        setConfigAnalysis(result);
      } catch (err) {
        console.error('[analyzeConfig]', err);
      } finally {
        setAnalyzingConfig(false);
      }
    }, 800);
    setAnalyzeTimer(timer);
  };

  const saveBackup = async () => {
    if (!selectedSwitch) return;

    if (backupData.backup_type === 'text' && !backupData.config_text) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errConfigRequired'));
      return;
    }

    if (backupData.backup_type === 'file' && !backupFile) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errBackupFileRequired'));
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('backup_type', backupData.backup_type);
      formData.append('description', backupData.description);
      
      if (backupData.backup_type === 'text') {
        formData.append('config_text', backupData.config_text);
      } else if (backupFile) {
        formData.append('backup_file', backupFile);
      }
      
      await apiService.createSwitchBackup(selectedSwitch.id, formData);
      showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successBackupUploaded'));

      await loadBackupHistory(selectedSwitch.id);
      await loadData();

      setBackupData({ backup_type: 'text', config_text: '', description: '' });
      setBackupFile(null);
    } catch (error: any) {
      console.error('Error saving backup:', error);
      showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errUploadingBackup'));
    }
  };

  const exportVLANs = () => {
    const filteredVLANs = vlansData.filter(vlan =>
      String(vlan.vlan_id || '').toString().includes(vlanSearchTerm) ||
      String(vlan.vlan_name || '').toLowerCase().includes(vlanSearchTerm.toLowerCase()) ||
      (vlan.location_name && String(vlan.location_name).toLowerCase().includes(vlanSearchTerm.toLowerCase()))
    );
    
    const csvContent = [
      language === 'en'
        ? ['VLAN ID', 'VLAN Name', 'Location', 'Network Address', 'Subnet Mask', 'Gateway', 'IP Range', 'Description']
        : ['VLAN ID', 'VLAN Adı', 'Lokasyon', 'Network Adresi', 'Subnet Mask', 'Gateway', 'IP Aralığı', 'Açıklama'],
      ...filteredVLANs.map(vlan => [
        vlan.vlan_id,
        vlan.vlan_name,
        vlan.location_name || '',
        vlan.network_address || '',
        vlan.subnet_mask || '',
        vlan.gateway || '',
        vlan.ip_range || '',
        vlan.description || ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `vlan-listesi-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportSwitchModels = () => {
    const filteredModels = switchModelsData.filter(model =>
      String(model.brand || '').toLowerCase().includes(modelSearchTerm.toLowerCase()) ||
      String(model.model || '').toLowerCase().includes(modelSearchTerm.toLowerCase())
    );
    
    const csvContent = [
      language === 'en'
        ? ['Brand', 'Model', 'Port Count', 'Port Type', 'PoE Support', 'PoE Budget', 'SFP Ports', 'Description']
        : ['Marka', 'Model', 'Port Sayısı', 'Port Tipi', 'PoE Desteği', 'PoE Budget', 'SFP Portları', 'Açıklama'],
      ...filteredModels.map(model => [
        model.brand,
        model.model,
        model.port_count,
        model.port_type,
        model.poe_support ? t('network.yes') : t('network.no'),
        model.poe_budget_watts || 0,
        model.sfp_ports || 0,
        model.description || ''
      ])
    ].map(row => row.join(',')).join('\n');
    
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `switch-modelleri-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const handleSaveModel = async () => {
    if (!modelData.brand || !modelData.model) {
      showGlobalNotification.error(t('notif.filter.error'), t('network.errModelRequired'));
      return;
    }
    
    try {
      const formData = new FormData();
      Object.keys(modelData).forEach(key => {
        formData.append(key, modelData[key as keyof typeof modelData].toString());
      });
      
      if (modelImageFile) {
        formData.append('image', modelImageFile);
      }
      
      if (editingModel) {
        await apiService.updateSwitchModel(editingModel.id, formData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successModelUpdated'));
      } else {
        await apiService.createSwitchModel(formData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successModelCreated'));
      }

      await loadData();
      setShowModelModal(false);
    } catch (error: any) {
      console.error('Error saving model:', error);
      showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errSavingModel'));
    }
  };

  const handleDeleteModel = async (id: number) => {
    const modelToDelete = switchModelsData.find(m => m.id === id);
    if (!modelToDelete) return;
    
    if (confirm(language === 'en' ? `Are you sure you want to delete the "${modelToDelete.brand} ${modelToDelete.model}" model?` : `"${modelToDelete.brand} ${modelToDelete.model}" modelini silmek istediğinizden emin misiniz?`)) {
      try {
        await apiService.deleteSwitchModel(id);
        await loadData();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', language === 'en' ? 'Model deleted' : 'Model silindi');
      } catch (error: any) {
        console.error('Error deleting model:', error);
        showGlobalNotification.error(t('notif.filter.error'), error.message || t('network.errSavingModel'));
      }
    }
  };

  const handleEditModel = (model: any) => {
    setEditingModel(model);
    setModelData({
      brand: model.brand,
      model: model.model,
      port_count: model.port_count,
      port_type: model.port_type,
      poe_support: model.poe_support,
      poe_budget_watts: model.poe_budget_watts || 0,
      port_speeds: model.port_speeds || '',
      sfp_ports: model.sfp_ports || 0,
      technical_specs: model.technical_specs || '',
      description: model.description || ''
    });
    setModelImageFile(null);
    setShowModelListModal(false);
    setShowModelModal(true);
  };

  const handleSwitchLabelPrint = (sw: NetworkSwitch) => {
    setSelectedSwitchForLabel(sw);
    setShowLabelModal(true);
  };

  const printSwitchLabel = async () => {
    if (!selectedSwitchForLabel) return;

    try {
      // Get QR code
      const token = localStorage.getItem('auth_token');
      let qrCodeHTML = '';

      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_URL}/network/${selectedSwitchForLabel.id}/qr-code?token=${token}&embed=true`
        );

        if (response.ok) {
          qrCodeHTML = await response.text();
          // Extract just the img tag
          const imgMatch = qrCodeHTML.match(/<img\s+src="[^"]+"\s+alt="[^"]*"\s*\/>/);
          if (imgMatch) {
            qrCodeHTML = imgMatch[0];
          }
        }
      } catch (error) {
        console.error('Error fetching QR code:', error);
      }

      const printWindow = window.open('', '_blank');
      if (!printWindow) return;

      const labelHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${language === 'en' ? 'Switch Label' : 'Switch Etiketi'} - ${selectedSwitchForLabel.switch_name}</title>
          <style>
            @page {
              size: ${labelSettings.width}mm ${labelSettings.height}mm;
              margin: 0;
            }
            @media print {
              html, body {
                width: ${labelSettings.width}mm;
                height: ${labelSettings.height}mm;
                margin: 0;
                padding: 0;
              }
            }
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body {
              font-family: 'Arial', sans-serif;
              background: white;
              width: ${labelSettings.width}mm !important;
              height: ${labelSettings.height}mm !important;
              padding: ${labelSettings.margin_top}mm ${labelSettings.margin_right}mm ${labelSettings.margin_bottom}mm ${labelSettings.margin_left}mm;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            .label {
              width: ${labelSettings.width - 4}mm;
              height: ${labelSettings.height - 4}mm;
              border: 2px solid #059669;
              border-radius: 3mm;
              padding: 3mm;
              background: white;
              display: flex;
              flex-direction: row;
              align-items: center;
            }
            .left-section {
              flex: 1;
              ${labelSettings.orientation === 'vertical' ? 'padding-bottom: 3mm;' : 'padding-right: 3mm;'}
            }
            .switch-name {
              font-size: 18px;
              font-weight: bold;
              color: #065f46;
              margin-bottom: 2mm;
              word-wrap: break-word;
            }
            .ip-address {
              font-size: 14px;
              font-weight: bold;
              color: #374151;
              font-family: 'Courier New', monospace;
              background: #f0fdf4;
              padding: 1mm 2mm;
              border-radius: 2mm;
              display: inline-block;
              border: 1px solid #10b981;
              margin-bottom: 2mm;
            }
            .info-grid {
              display: grid;
              grid-template-columns: 1fr;
              gap: 1mm;
            }
            .info-item {
              background: #f9fafb;
              padding: 1.5mm;
              border-radius: 2mm;
              border-left: 2px solid #10b981;
            }
            .info-label {
              font-size: 10px;
              color: #6b7280;
              text-transform: uppercase;
              font-weight: bold;
              margin-bottom: 0.5mm;
            }
            .info-value {
              font-size: 11px;
              color: #111827;
              font-weight: bold;
              word-wrap: break-word;
            }
            .right-section {
              width: 20mm;
              text-align: center;
              border-left: 1px solid #d1d5db;
              padding-left: 2mm;
            }
            .qr-section {
              font-size: 12px;
              font-weight: bold;
              color: #000000;
            }
            .network-icon {
              width: 4mm;
              height: 4mm;
              background: #10b981;
              border-radius: 1mm;
              display: inline-block;
              margin-right: 2mm;
            }
            .qr-code {
              width: 15mm;
              height: 15mm;
              border-radius: 2mm;
              margin-bottom: 2mm;
              display: flex;
              align-items: center;
              justify-content: center;
            }
            .qr-code img {
              width: 100%;
              height: 100%;
              object-fit: contain;
            }
            .qr-placeholder {
              width: 15mm;
              height: 15mm;
              background: #e5e7eb;
              border: 1px solid #d1d5db;
              border-radius: 2mm;
              display: inline-block;
              margin-bottom: 2mm;
            }
            @media print {
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="left-section">
              <div class="switch-name">
                <span class="network-icon"></span>
                ${selectedSwitchForLabel.switch_name}
              </div>
              <div class="ip-address">${selectedSwitchForLabel.ip_address}</div>

              <div class="info-grid">
                <div class="info-item">
                  <div class="info-label">Model</div>
                  <div class="info-value">${selectedSwitchForLabel.brand} ${selectedSwitchForLabel.model}</div>
                </div>

                <div class="info-item">
                  <div class="info-label">Port</div>
                  <div class="info-value">${selectedSwitchForLabel.port_count}P</div>
                </div>

                <div class="info-item">
                  <div class="info-label">${language === 'en' ? 'Location' : 'Lokasyon'}</div>
                  <div class="info-value">${selectedSwitchForLabel.location?.name || 'N/A'}</div>
                </div>
              </div>
            </div>

            <div class="right-section">
              <div class="qr-code">
                ${qrCodeHTML ? qrCodeHTML : '<div class="qr-placeholder"></div>'}
              </div>
              <div class="qr-section">
                <strong>IT Toolbox</strong><br>
                <strong>${new Date().toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}</strong>
              </div>
            </div>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(labelHTML);
      printWindow.document.close();

      setTimeout(() => {
        printWindow.print();
      }, 1000);

      setShowLabelModal(false);
    } catch (error) {
      console.error('Error printing switch label:', error);
      showGlobalNotification.error(t('notif.filter.error'), t('network.errLabelPrint'));
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('network.pageTitle')}</h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">{t('network.pageSubtitle')}</p>
        </div>
        <div className="grid grid-cols-1 sm:flex sm:flex-row gap-2 w-full sm:w-auto">
          <button
            onClick={exportToExcel}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
            title={language === 'en' ? 'Export to Excel' : "Excel'e Aktar"}
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </button>
          <button
            onClick={exportToPDF}
            className="flex items-center justify-center px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px] text-sm"
            title={language === 'en' ? 'Export to PDF' : "PDF'e Aktar"}
          >
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </button>
          <button
            onClick={() => setShowVlanModal(true)}
            className="flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors min-h-[44px] text-sm"
          >
            <Network className="w-4 h-4 mr-2" />
            {t('network.vlanMgmt')}
          </button>
          <button
            onClick={() => {
              setShowModelListModal(true);
              loadData();
            }}
            className="flex items-center justify-center px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors min-h-[44px] text-sm"
          >
            <Settings className="w-4 h-4 mr-2" />
            {t('network.defineModel')}
          </button>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'switch-config-wizard' }))}
            className="flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors min-h-[44px] text-sm"
            title={language === 'en' ? 'Aruba Switch Configuration Wizard' : 'Aruba Switch Konfigürasyon Sihirbazı'}
          >
            <Zap className="w-4 h-4 mr-2" />
            Config Wizard
          </button>
          <button
            onClick={handleAdd}
            className="flex items-center justify-center px-4 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors min-h-[44px] text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('network.newSwitch')}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 mb-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('network.statTotal')}</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{switchesData.length}</p>
            </div>
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Network className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('network.statActive')}</p>
              <p className="text-2xl font-bold text-green-600">
                {switchesData.filter(s => s.status === 'active').length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('network.statTotalPorts')}</p>
              <p className="text-2xl font-bold text-primary">
                {switchesData.reduce((sum, s) => sum + s.port_count, 0)}
              </p>
            </div>
            <div className="w-12 h-12 bg-primary/20 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-primary" />
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-1">{t('network.statPoe')}</p>
              <p className="text-2xl font-bold text-orange-600">
                {switchesData.filter(s => s.poe_support).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder={t('network.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
          />
        </div>
        
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('network.filterAllStatuses')}</option>
          {statusOptions.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>

        <select
          value={filterHealth}
          onChange={(e) => setFilterHealth(e.target.value)}
          className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
        >
          <option value="all">{t('network.filterAllHealth')}</option>
          {healthOptions.map(health => (
            <option key={health.value} value={health.value}>{health.label}</option>
          ))}
        </select>
      </div>

      {/* Switches List */}
      <div className="space-y-3">
        {paginatedSwitches.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
            <Network className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">{t('network.noSwitchFound')}</p>
          </div>
        ) : (
          paginatedSwitches.map((sw) => (
          <div
            key={sw.id}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
              {/* Left: Icon, Name, Model */}
              <div className="flex items-center gap-3 flex-1 min-w-0 w-full sm:w-auto">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-indigo-100 dark:bg-indigo-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Network className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {sw.switch_name}
                  </h3>
                  {sw.alias && (
                    <p className="text-xs text-indigo-600 dark:text-indigo-400 font-medium truncate">
                      {sw.alias}
                    </p>
                  )}
                  <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{sw.brand} {sw.model}</p>
                </div>
              </div>

              {/* Middle: Info Grid - Desktop only */}
              <div className="hidden lg:grid grid-cols-4 gap-4 flex-shrink-0">
                {/* IP Address */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('network.colIpAddress')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{sw.ip_address}</p>
                </div>

                {/* Port Status */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('network.colPorts')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    <span className="text-green-600">{sw.active_ports || 0}</span>
                    <span className="text-gray-400 mx-1">/</span>
                    <span>{sw.port_count}</span>
                  </p>
                </div>

                {/* Location */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('network.colLocation')}</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{sw.location?.name}</p>
                </div>

                {/* Status */}
                <div className="text-center">
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{t('network.colStatus')}</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sw.status)}`}>
                    {statusOptions.find(s => s.value === sw.status)?.label}
                  </span>
                </div>
              </div>

              {/* Right: Actions */}
              <div className="flex flex-wrap gap-1 sm:gap-2 w-full sm:w-auto justify-end">
                {/* Ana butonlar - her zaman görünür */}
                <button
                  onClick={() => showSwitchDetail(sw)}
                  className="p-1.5 sm:p-1 text-gray-400 hover:text-primary transition-colors"
                  title={language === 'en' ? 'View Details' : 'Detayları Görüntüle'}
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleCopy(sw)}
                  className="p-1.5 sm:p-1 text-gray-400 hover:text-green-600 transition-colors"
                  title={language === 'en' ? 'Copy' : 'Kopyala'}
                >
                  <Copy className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleEdit(sw)}
                  className="p-1.5 sm:p-1 text-gray-400 hover:text-primary transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(sw.id)}
                  className="p-1.5 sm:p-1 text-gray-400 hover:text-red-600 transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>

                {/* Etiket butonu - mobilde de görünür */}
                <button
                  onClick={() => handleSwitchLabelPrint(sw)}
                  className="p-1.5 sm:p-1 text-gray-400 hover:text-purple-600 transition-colors"
                  title={language === 'en' ? 'Print Label' : 'Etiket Bas'}
                >
                  <Printer className="w-4 h-4" />
                </button>

                {/* İkincil butonlar - mobilde gizli, masaüstünde görünür */}
                <div className="hidden sm:flex space-x-2">
                  <button
                    onClick={() => showPortManagement(sw)}
                    className="p-1 text-gray-400 hover:text-primary transition-colors"
                    title={language === 'en' ? 'Port Management' : 'Port Yönetimi'}
                  >
                    <Settings className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setSelectedSwitchForMaintenance(sw);
                      setMaintenanceData({
                        maintenance_type: 'cleaning',
                        maintenance_date: new Date().toISOString().split('T')[0],
                        notes: ''
                      });
                      setShowMaintenanceModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                    title={language === 'en' ? 'Maintenance Record' : 'Bakım Kaydı'}
                  >
                    <Calendar className="w-4 h-4" />
                  </button>
                  {['admin', 'superadmin'].includes(user?.role) && (
                    <button
                      onClick={async () => {
                        setSelectedSwitch(sw);
                        await loadBackupHistory(sw.id);
                        setShowBackupModal(true);
                      }}
                      className={`p-1 transition-colors ${(() => {
                        if (!sw.last_backup_date) return 'text-red-500 hover:text-red-700';
                        const daysDiff = (Date.now() - new Date(sw.last_backup_date).getTime()) / (1000 * 60 * 60 * 24);
                        if (daysDiff <= 180) return 'text-green-500 hover:text-green-700';
                        if (daysDiff <= 365) return 'text-purple-500 hover:text-purple-700';
                        return 'text-red-500 hover:text-red-700';
                      })()}`}
                      title={sw.last_backup_date
                        ? `${language === 'en' ? 'Last backup' : 'Son backup'}: ${new Date(sw.last_backup_date).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                        : t('network.lastBackupNever')}
                    >
                      <Database className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile/Tablet Info - görünür sadece lg altında */}
            <div className="lg:hidden mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('network.colIpAddress')}:</span>
                <p className="mt-1 font-medium text-gray-900 dark:text-gray-100 break-all">{sw.ip_address}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('network.colPorts')}:</span>
                <p className="mt-1 font-medium">
                  <span className="text-green-600">{sw.active_ports || 0}</span>
                  <span className="text-gray-400 mx-1">/</span>
                  <span className="text-gray-900 dark:text-gray-100">{sw.port_count}</span>
                </p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('network.colLocation')}:</span>
                <p className="mt-1 font-medium text-gray-900 dark:text-gray-100 truncate">{sw.location?.name || '-'}</p>
              </div>
              <div>
                <span className="text-gray-500 dark:text-gray-400 text-xs">{t('network.colStatus')}:</span>
                <p className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(sw.status)}`}>
                    {statusOptions.find(s => s.value === sw.status)?.label}
                  </span>
                </p>
              </div>
            </div>
          </div>
        ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 px-4 py-3">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            {/* Mobile: Simple pagination */}
            <div className="flex sm:hidden items-center justify-between w-full">
              <button
                onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {t('network.prevPage')}
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'en' ? `Page ${currentPage} / ${totalPages}` : `Sayfa ${currentPage} / ${totalPages}`}
              </span>
              <button
                onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 dark:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                {t('network.nextPage')}
                <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>

            {/* Desktop: Full pagination */}
            <div className="hidden sm:flex items-center justify-between w-full">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                {language === 'en'
                  ? <>Showing <span className="font-medium">{startIndex + 1}-{Math.min(endIndex, filteredSwitches.length)}</span> of <span className="font-medium">{filteredSwitches.length}</span> switches</>
                  : <>Toplam <span className="font-medium">{filteredSwitches.length}</span> switch'ten{' '}<span className="font-medium">{startIndex + 1}-{Math.min(endIndex, filteredSwitches.length)}</span>{' '}arası gösteriliyor</>
                }
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  {t('network.prevPage')}
                </button>

                {/* Page numbers */}
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-700 border border-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                >
                  {t('network.nextPage')}
                  <ChevronRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Switch Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingSwitch ? t('network.editSwitch') : t('network.newSwitchTitle')}
                </h2>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 sm:space-y-6 max-h-[70vh] sm:max-h-none overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelCabinet')}
                  </label>
                  <select
                    value={switchData.cabinet_id}
                    onChange={async (e) => {
                      const cabinetId = parseInt(e.target.value);
                      const selectedCabinet = cabinetsData.find(c => c.id === cabinetId);
                      
                      if (selectedCabinet) {
                        // Aynı kabindeki mevcut switch'leri kontrol et
                        const existingSwitches = switchesData.filter(sw => 
                          sw.cabinet_id === cabinetId && 
                          (!editingSwitch || sw.id !== editingSwitch.id)
                        );
                        
                        // SW01, SW02, SW03... formatında en küçük boş numarayı bul
                        let switchNumber = 1;
                        let switchName = '';
                        
                        do {
                          const paddedNumber = switchNumber.toString().padStart(2, '0');
                          switchName = `${selectedCabinet.cabinet_name}-SW${paddedNumber}`;
                          switchNumber++;
                        } while (existingSwitches.some(sw => sw.switch_name === switchName));
                        
                        setSwitchData({
                          ...switchData, 
                          cabinet_id: cabinetId,
                          switch_name: switchName
                        });
                      } else {
                        setSwitchData({
                          ...switchData, 
                          cabinet_id: cabinetId,
                          switch_name: ''
                        });
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('network.selectCabinet')}</option>
                    {cabinetsData.map((cabinet) => (
                      <option key={cabinet.id} value={cabinet.id}>
                        {cabinet.cabinet_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelSwitchName')}
                  </label>
                  <input
                    type="text"
                    value={switchData.switch_name}
                    onChange={(e) => setSwitchData({...switchData, switch_name: e.target.value})}
                    onBlur={(e) => {
                      const switchName = e.target.value;
                      if (switchName && switchData.cabinet_id) {
                        // Aynı kabindeki aynı isimdeki switch'i kontrol et
                        const duplicateSwitch = switchesData.find(sw =>
                          sw.cabinet_id === switchData.cabinet_id &&
                          sw.switch_name === switchName &&
                          (!editingSwitch || sw.id !== editingSwitch.id)
                        );

                        if (duplicateSwitch) {
                          showGlobalNotification.error(t('notif.filter.error'), language === 'en' ? `A switch named "${switchName}" already exists in this cabinet` : `Bu kabinde "${switchName}" adlı switch zaten mevcut`);
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="TR-ALI-XA11-SW01"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelAlias')}
                  </label>
                  <input
                    type="text"
                    value={switchData.alias}
                    onChange={(e) => setSwitchData({...switchData, alias: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Ana Bina Switch"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelBrand')}
                  </label>
                  <input
                    type="text"
                    value={switchData.brand}
                    onChange={(e) => setSwitchData({...switchData, brand: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Cisco"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelModel')}
                  </label>
                  <select
                    value={`${switchData.brand}-${switchData.model}`}
                    onChange={(e) => {
                      const selectedModel = switchModelsData.find(m => `${m.brand}-${m.model}` === e.target.value);
                      if (selectedModel) {
                        setSwitchData({
                          ...switchData,
                          brand: selectedModel.brand,
                          model: selectedModel.model,
                          port_count: selectedModel.port_count,
                          port_type: selectedModel.port_type,
                          poe_support: selectedModel.poe_support,
                          poe_budget_watts: selectedModel.poe_budget_watts || 0
                        });
                        // LLDP komşu sayısını SFP port sayısına göre sınırla
                        const maxNeighbors = selectedModel.sfp_ports || selectedModel.port_count;
                        if (lldpNeighbors.length > maxNeighbors) {
                          setLldpNeighbors(lldpNeighbors.slice(0, maxNeighbors));
                        }
                      }
                    }}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="">{t('network.selectModel')}</option>
                    {switchModelsData.map((model) => (
                      <option key={model.id} value={`${model.brand}-${model.model}`}>
                        {model.brand} {model.model} ({model.port_count} port)
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelSerialNo')}
                  </label>
                  <input
                    type="text"
                    value={switchData.serial_number}
                    onChange={(e) => setSwitchData({...switchData, serial_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="SN123456789"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelIpAddr')}
                  </label>
                  <input
                    type="text"
                    value={switchData.ip_address}
                    onChange={(e) => setSwitchData({...switchData, ip_address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="192.168.1.2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelMacAddr')}
                  </label>
                  <input
                    type="text"
                    value={switchData.mac_address}
                    onChange={(e) => setSwitchData({...switchData, mac_address: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="00:1A:2B:3C:4D:5E"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelInventoryNo')}
                  </label>
                  <input
                    type="text"
                    value={switchData.inventory_number}
                    onChange={(e) => setSwitchData({...switchData, inventory_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="INV-SW-001"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelFirmware')}
                  </label>
                  <input
                    type="text"
                    value={switchData.firmware_version}
                    onChange={(e) => setSwitchData({...switchData, firmware_version: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="15.2(4)S7"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPortCount')}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={switchData.port_count}
                    onChange={(e) => setSwitchData({...switchData, port_count: parseInt(e.target.value) || 24})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPortType')}
                  </label>
                  <select
                    value={switchData.port_type}
                    onChange={(e) => setSwitchData({...switchData, port_type: e.target.value as NetworkSwitch['port_type']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {portTypeOptions.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelCompany')}
                  </label>
                  <select
                    value={switchData.company_id}
                    onChange={(e) => setSwitchData({...switchData, company_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('network.selectCompany')}</option>
                    {companiesData.map((company) => (
                      <option key={company.id} value={company.id}>
                        {company.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelLocation')}
                  </label>
                  <select
                    value={switchData.location_id}
                    onChange={(e) => setSwitchData({...switchData, location_id: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={0}>{t('network.selectLocation')}</option>
                    {locationsData.map((location) => (
                      <option key={location.id} value={location.id}>
                        {location.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelRackPos')}
                  </label>
                  <input
                    type="text"
                    value={switchData.rack_position}
                    onChange={(e) => setSwitchData({...switchData, rack_position: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="U1-U2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelMgmtVlan')}
                  </label>
                  <select
                    value={switchData.management_vlan}
                    onChange={(e) => setSwitchData({...switchData, management_vlan: parseInt(e.target.value) || 1})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value={1}>VLAN 1 (Default)</option>
                    {vlansData.filter(vlan => vlan.location_id === switchData.location_id).map((vlan) => (
                      <option key={vlan.id} value={vlan.vlan_id}>
                        VLAN {vlan.vlan_id} - {vlan.vlan_name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelUplinkPorts')}
                  </label>
                  <input
                    type="text"
                    value={switchData.uplink_ports}
                    onChange={(e) => setSwitchData({...switchData, uplink_ports: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Gi0/47, Gi0/48"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelStatus')}
                  </label>
                  <select
                    value={switchData.status}
                    onChange={(e) => setSwitchData({...switchData, status: e.target.value as NetworkSwitch['status']})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    {statusOptions.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPurchaseDate')}
                  </label>
                  <input
                    type="date"
                    value={switchData.purchase_date}
                    onChange={(e) => setSwitchData({...switchData, purchase_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelWarrantyEnd')}
                  </label>
                  <input
                    type="date"
                    value={switchData.warranty_end_date}
                    onChange={(e) => setSwitchData({...switchData, warranty_end_date: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPurchasePrice')}
                  </label>
                  <div className="flex">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={switchData.purchase_price}
                      onChange={(e) => setSwitchData({...switchData, purchase_price: parseFloat(e.target.value) || 0})}
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-l-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="0.00"
                    />
                    <select
                      value={switchData.currency}
                      onChange={(e) => setSwitchData({...switchData, currency: e.target.value as 'TRY' | 'USD' | 'EUR'})}
                      className="px-3 py-2 border border-l-0 border-gray-300 dark:border-gray-600 rounded-r-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="TRY">TRY</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingModel(null);
                      setModelData({
                        brand: '',
                        model: '',
                        port_count: 24,
                        port_type: 'gigabit',
                        poe_support: false,
                        poe_budget_watts: 0,
                        port_speeds: '',
                        sfp_ports: 0,
                        technical_specs: '',
                        description: ''
                      });
                      setModelImageFile(null);
                      setShowModelModal(true);
                    }}
                    className="w-full px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    {t('network.defineModelBtn')}
                  </button>
                </div>
              </div>

              {currentSelectedModel ? (
                <div className="col-span-1 sm:col-span-2 bg-primary/10 dark:bg-primary/20 border border-primary/30 dark:border-primary-dark rounded-md px-4 py-3">
                  <p className="text-sm font-medium text-primary-dark dark:text-accent mb-1">{t('network.modelAutoApplied')}</p>
                  <p className="text-sm text-primary dark:text-primary">
                    PoE: {currentSelectedModel.poe_support ? `${t('network.poeSupportedWith')}${currentSelectedModel.poe_budget_watts ? ` — ${currentSelectedModel.poe_budget_watts}W ${t('network.poeWithBudget')}` : ''}` : t('network.poeNotSupported')}
                    {currentSelectedModel.image_path && ` · ${t('network.modelImageFromModel')}`}
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={switchData.poe_support}
                      onChange={(e) => setSwitchData({...switchData, poe_support: e.target.checked})}
                      className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('network.poeSupportLabel')}</span>
                  </div>

                  {switchData.poe_support && (
                    <div className="col-span-1 sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.poeBudgetLabel')}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={switchData.poe_budget_watts}
                        onChange={(e) => setSwitchData({...switchData, poe_budget_watts: parseInt(e.target.value) || 0})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  )}

                  <div className="col-span-1 sm:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.switchImageLabel')}
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </>
              )}



              {['admin', 'superadmin'].includes(user?.role) && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.adminUsernameLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={switchData.admin_username}
                        onChange={(e) => setSwitchData({...switchData, admin_username: e.target.value})}
                        autoComplete="off"
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="admin"
                      />
                      {switchData.admin_username && (
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText(switchData.admin_username)}
                          className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                          title={language === 'en' ? 'Copy' : 'Kopyala'}
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.adminPasswordLabel')}
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={switchData.admin_password}
                        onChange={(e) => setSwitchData({...switchData, admin_password: e.target.value})}
                        autoComplete="new-password"
                        className="w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="••••••••"
                      />
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex space-x-1">
                        {switchData.admin_password && (
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(switchData.admin_password)}
                            className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                            title={language === 'en' ? 'Copy' : 'Kopyala'}
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="p-1 text-gray-400 hover:text-gray-600 dark:text-gray-400"
                          title={showPassword ? (language === 'en' ? 'Hide' : 'Gizle') : (language === 'en' ? 'Show' : 'Göster')}
                        >
                          {showPassword ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="col-span-1 sm:col-span-2">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 space-y-2 sm:space-y-0">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{t('network.lldpNeighborsTitle')}</h3>
                  <button
                    type="button"
                    onClick={() => {
                      const selectedModel = switchModelsData.find(m => `${m.brand}-${m.model}` === `${switchData.brand}-${switchData.model}`);
                      const maxNeighbors = selectedModel ? (selectedModel.sfp_ports || selectedModel.port_count) : 24;
                      
                      if (lldpNeighbors.length < maxNeighbors) {
                        setLldpNeighbors([...lldpNeighbors, { neighbor_switch_id: 0, local_port: '', remote_port: '' }]);
                      } else {
                        showGlobalNotification.warning(language === 'en' ? 'Warning' : 'Uyarı', language === 'en' ? `Maximum ${maxNeighbors} LLDP neighbors can be added for this model` : `Bu model için maksimum ${maxNeighbors} LLDP komşusu eklenebilir`);
                      }
                    }}
                    className="w-full sm:w-auto px-3 py-1 bg-primary text-white rounded text-sm hover:bg-primary-dark transition-colors"
                  >
                    {t('network.addLldpNeighbor')}
                  </button>
                </div>
                <div className="space-y-3">
                  {lldpNeighbors.map((neighbor, index) => (
                    <div key={index} className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-3 border border-gray-200 dark:border-gray-700 dark:border-gray-600 rounded-md">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {t('network.neighborSwitch')}
                        </label>
                        <select
                          value={neighbor.neighbor_switch_id}
                          onChange={(e) => {
                            const newNeighbors = [...lldpNeighbors];
                            newNeighbors[index].neighbor_switch_id = parseInt(e.target.value);
                            setLldpNeighbors(newNeighbors);
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        >
                          <option value={0}>{language === 'en' ? 'Select' : 'Seçin'}</option>
                          {switchesData.filter(sw => !editingSwitch || sw.id !== editingSwitch.id).map((sw) => (
                            <option key={sw.id} value={sw.id}>
                              {sw.switch_name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {t('network.localPort')}
                        </label>
                        <input
                          type="text"
                          value={neighbor.local_port}
                          onChange={(e) => {
                            const newNeighbors = [...lldpNeighbors];
                            newNeighbors[index].local_port = e.target.value;
                            setLldpNeighbors(newNeighbors);
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Gi0/1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {t('network.remotePort')}
                        </label>
                        <input
                          type="text"
                          value={neighbor.remote_port}
                          onChange={(e) => {
                            const newNeighbors = [...lldpNeighbors];
                            newNeighbors[index].remote_port = e.target.value;
                            setLldpNeighbors(newNeighbors);
                          }}
                          className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Gi0/2"
                        />
                      </div>
                      <div className="flex items-end sm:col-span-1">
                        <button
                          type="button"
                          onClick={() => {
                            const newNeighbors = lldpNeighbors.filter((_, i) => i !== index);
                            setLldpNeighbors(newNeighbors.length > 0 ? newNeighbors : [{ neighbor_switch_id: 0, local_port: '', remote_port: '' }]);
                          }}
                          className="w-full sm:w-auto px-2 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 transition-colors"
                        >
                          {t('network.delete')}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('network.cancel')}
              </button>
              <button
                onClick={handleSaveAsync}
                className="w-full sm:w-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingSwitch ? t('network.update') : t('network.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Switch Detail Modal */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)}>
          {selectedSwitch && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {t('network.detailTitle')}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {selectedSwitch.switch_name}
                  </p>
                </div>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:text-gray-400"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('network.switchInfoTitle')}</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('network.detailSwitchName')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSwitch.switch_name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('network.detailBrandModel')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSwitch.brand} {selectedSwitch.model}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('network.detailIpAddr')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSwitch.ip_address}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('network.detailPortCount')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSwitch.port_count}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600 dark:text-gray-400">{t('network.detailPoeSupport')}</span>
                      <span className="font-medium text-gray-900 dark:text-gray-100">{selectedSwitch.poe_support ? t('network.yes') : t('network.no')}</span>
                    </div>
                  </div>
                </div>

                {selectedSwitch.image_path && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">{t('network.switchImageTitle')}</h3>
                    <div className="flex justify-center">
                      <img
                        src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedSwitch.image_path}`}
                        alt={selectedSwitch.switch_name}
                        className="max-w-xs max-h-48 object-contain rounded-lg border border-gray-200 dark:border-gray-700 cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => {
                          setSelectedImage(`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${selectedSwitch.image_path}`);
                          setShowImageModal(true);
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Image Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[60] p-4">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <button
              onClick={() => setShowImageModal(false)}
              className="absolute top-4 right-4 z-10 p-2 bg-black bg-opacity-50 text-white rounded-full hover:bg-opacity-70 transition-colors"
            >
              ✕
            </button>
            <img
              src={selectedImage}
              alt={t('network.switchImageTitle')}
              className="w-full h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}

      {/* Model Management Modal */}
      <Modal isOpen={showModelModal} onClose={() => setShowModelModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {editingModel ? t('network.editModel') : t('network.newModelTitle')}
                </h2>
                <button
                  onClick={() => setShowModelModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6 space-y-4 max-h-[70vh] sm:max-h-none overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelBrand')}
                  </label>
                  <input
                    type="text"
                    value={modelData.brand}
                    onChange={(e) => setModelData({...modelData, brand: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Cisco"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelModel')}
                  </label>
                  <input
                    type="text"
                    value={modelData.model}
                    onChange={(e) => setModelData({...modelData, model: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Catalyst 2960-X"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPortCount')}
                  </label>
                  <input
                    type="number"
                    value={modelData.port_count}
                    onChange={(e) => setModelData({...modelData, port_count: parseInt(e.target.value) || 24})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelPortType')}
                  </label>
                  <select
                    value={modelData.port_type}
                    onChange={(e) => setModelData({...modelData, port_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  >
                    <option value="gigabit">Gigabit Ethernet</option>
                    <option value="fast_ethernet">Fast Ethernet</option>
                    <option value="10g">10 Gigabit</option>
                    <option value="sfp">SFP/SFP+</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelSfpPorts')}
                  </label>
                  <input
                    type="number"
                    value={modelData.sfp_ports}
                    onChange={(e) => setModelData({...modelData, sfp_ports: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={modelData.poe_support}
                  onChange={(e) => setModelData({...modelData, poe_support: e.target.checked})}
                  className="rounded border-gray-300 dark:border-gray-600 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('network.poeSupportLabel')}</span>
              </div>

              {modelData.poe_support && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.poeBudgetLabel')}
                  </label>
                  <input
                    type="number"
                    value={modelData.poe_budget_watts}
                    onChange={(e) => setModelData({...modelData, poe_budget_watts: parseInt(e.target.value) || 0})}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.labelPortSpeeds')}
                </label>
                <input
                  type="text"
                  value={modelData.port_speeds}
                  onChange={(e) => setModelData({...modelData, port_speeds: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="1Gbps, 10Gbps"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.labelModelImage')}
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setModelImageFile(e.target.files?.[0] || null)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.labelTechSpecs')}
                </label>
                <textarea
                  value={modelData.technical_specs}
                  onChange={(e) => setModelData({...modelData, technical_specs: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Teknik özellikler ve detaylar..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.labelDescription')}
                </label>
                <textarea
                  value={modelData.description}
                  onChange={(e) => setModelData({...modelData, description: e.target.value})}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Model hakkında açıklama..."
                />
              </div>
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => setShowModelModal(false)}
                className="w-full sm:w-auto px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('network.cancel')}
              </button>
              <button
                onClick={handleSaveModel}
                className="w-full sm:w-auto px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                {editingModel ? t('network.update') : t('network.save')}
              </button>
            </div>
          </div>
        </Modal>

      {/* Model List Modal */}
      <Modal isOpen={showModelListModal} onClose={() => setShowModelListModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.modelMgmtTitle')}
                </h2>
                <button
                  onClick={() => setShowModelListModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <div className="flex items-center space-x-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'en' ? `${switchModelsData.length} models defined` : `Toplam ${switchModelsData.length} model tanımlı`}
                  </p>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder={t('network.searchModel')}
                      value={modelSearchTerm}
                      onChange={(e) => setModelSearchTerm(e.target.value)}
                      className="pl-8 pr-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={exportSwitchModels}
                    className="flex items-center px-3 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors text-sm"
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    {t('network.export')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingModel(null);
                      setModelData({
                        brand: '',
                        model: '',
                        port_count: 24,
                        port_type: 'gigabit',
                        poe_support: false,
                        poe_budget_watts: 0,
                        port_speeds: '',
                        sfp_ports: 0,
                        technical_specs: '',
                        description: ''
                      });
                      setModelImageFile(null);
                      setShowModelListModal(false);
                      setShowModelModal(true);
                    }}
                    className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('network.newModel')}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                {switchModelsData.filter(model =>
                  String(model.brand || '').toLowerCase().includes(modelSearchTerm.toLowerCase()) ||
                  String(model.model || '').toLowerCase().includes(modelSearchTerm.toLowerCase())
                ).map((model) => (
                  <div
                    key={model.id}
                    className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 border border-gray-200 dark:border-gray-700 dark:border-gray-600"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                          {model.brand} {model.model}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {model.port_count} port - {model.port_type}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        <button
                          onClick={() => handleEditModel(model)}
                          className="p-1 text-gray-400 hover:text-primary transition-colors"
                          title={language === 'en' ? 'Edit' : 'Düzenle'}
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteModel(model.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                          title={language === 'en' ? 'Delete' : 'Sil'}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {model.image_path && (
                      <div className="mb-3">
                        <img
                          src={`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${model.image_path}`}
                          alt={`${model.brand} ${model.model}`}
                          className="w-full h-24 object-contain rounded border border-gray-200 dark:border-gray-700 dark:border-gray-600"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    <div className="space-y-2 text-xs">
                      {model.poe_support && (
                        <div className="flex items-center text-orange-600">
                          <Zap className="w-3 h-3 mr-1" />
                          PoE: {model.poe_budget_watts}W
                        </div>
                      )}
                      {model.sfp_ports > 0 && (
                        <div className="text-gray-600 dark:text-gray-400">
                          {t('network.sfpPortsLabel')} {model.sfp_ports}
                        </div>
                      )}
                      {model.usage_count !== undefined && (
                        <div className="text-gray-600 dark:text-gray-400">
                          {t('network.usageLabel')} {model.usage_count} {language === 'en' ? 'switch(es)' : 'switch'}
                        </div>
                      )}
                      {model.description && (
                        <div className="text-gray-600 dark:text-gray-400">
                          {model.description}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {switchModelsData.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">{t('network.noModels')}</p>
                </div>
              )}
            </div>
          </div>
        </Modal>

      {/* Port Management Modal */}
      <Modal isOpen={showPortModal} onClose={() => setShowPortModal(false)}>
          {selectedSwitchForPorts && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.portMgmtTitle')} - {selectedSwitchForPorts.switch_name}
                </h2>
                <button
                  onClick={() => {
                    setShowPortModal(false);
                    setEditingPort(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {language === 'en'
                    ? `Total ${switchPorts.length} ports - Active: ${switchPorts.filter(p => p.is_active).length}`
                    : `Toplam ${switchPorts.length} port - Aktif: ${switchPorts.filter(p => p.is_active).length}`}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-h-96 overflow-y-auto">
                {switchPorts.map((port) => (
                  <div
                    key={port.id}
                    className={`p-4 rounded-lg border-2 transition-colors ${
                      port.is_active 
                        ? 'border-green-200 bg-green-50 dark:border-green-700 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:border-gray-600 dark:bg-gray-700'
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center">
                        <div className={`w-3 h-3 rounded-full mr-2 ${
                          port.is_active ? 'bg-green-500' : 'bg-gray-400'
                        }`}></div>
                        <span className="font-medium text-sm">
                          Port {port.port_number}
                        </span>
                      </div>
                      <button
                        onClick={() => handlePortEdit(port)}
                        className="p-1 text-gray-400 hover:text-primary transition-colors"
                        title="Düzenle"
                      >
                        <Edit2 className="w-3 h-3" />
                      </button>
                    </div>
                    
                    <div className="space-y-1 text-xs">
                      <div className="text-gray-600 dark:text-gray-400">
                        {t('network.portNameLabel')} {port.port_name || t('network.notSpecified')}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {t('network.connectedDeviceLabel')} {port.connected_device || (language === 'en' ? 'None' : 'Yok')}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {t('network.vlanLabel')} {port.vlan_number ? `${port.vlan_number} (${port.vlan_name})` : (language === 'en' ? 'None' : 'Yok')}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {t('network.ipLabel')} {port.ip_address || (language === 'en' ? 'None' : 'Yok')}
                      </div>
                      <div className="text-gray-600 dark:text-gray-400">
                        {t('network.descriptionLabel')} {port.description || (language === 'en' ? 'None' : 'Yok')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* Port Edit Modal */}
      <Modal isOpen={editingPort} onClose={() => setEditingPort(null)}>
          {editingPort && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {language === 'en' ? `Edit Port ${editingPort.port_number}` : `Port ${editingPort.port_number} Düzenle`}
                </h2>
                <button
                  onClick={() => setEditingPort(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.portNameField')}
                </label>
                <input
                  type="text"
                  value={editingPort.port_name || ''}
                  onChange={(e) => setEditingPort({...editingPort, port_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Port 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.connectedDeviceField')}
                </label>
                <input
                  type="text"
                  value={editingPort.connected_device || ''}
                  onChange={(e) => setEditingPort({...editingPort, connected_device: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Bilgisayar, Yazicı, vb."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.labelDescription')}
                </label>
                <textarea
                  value={editingPort.description || ''}
                  onChange={(e) => setEditingPort({...editingPort, description: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Port hakkında açıklama..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.locationField')}
                </label>
                <select
                  value={editingPort.selected_location_id || ''}
                  onChange={(e) => {
                    const locationId = e.target.value ? parseInt(e.target.value) : null;
                    setEditingPort({
                      ...editingPort, 
                      selected_location_id: locationId,
                      vlan_id: null,
                      ip_address: ''
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="">{t('network.selectLocationOpt')}</option>
                  {locationsData.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.vlanField')}
                </label>
                <select
                  value={editingPort.vlan_id || ''}
                  onChange={(e) => {
                    const vlanId = e.target.value ? parseInt(e.target.value) : null;
                    const selectedVlan = vlansData.find(v => v.id === vlanId);
                    let newIpPrefix = '';
                    
                    if (selectedVlan && selectedVlan.network_address) {
                      const [networkPart] = selectedVlan.network_address.split('/');
                      const segments = networkPart.split('.');
                      newIpPrefix = `${segments[0]}.${segments[1]}.`;
                    }
                    
                    setEditingPort({
                      ...editingPort, 
                      vlan_id: vlanId,
                      ip_address: newIpPrefix
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  disabled={!editingPort.selected_location_id}
                >
                  <option value="">{t('network.selectVlanOpt')}</option>
                  {vlansData.filter(vlan => vlan.location_id === editingPort.selected_location_id).map((vlan) => (
                    <option key={vlan.id} value={vlan.id}>
                      VLAN {vlan.vlan_id} - {vlan.vlan_name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.ipAddressField')}
                </label>
                <input
                  type="text"
                  value={editingPort.ip_address || ''}
                  onChange={(e) => setEditingPort({...editingPort, ip_address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="192.168.1.10"
                  disabled={!editingPort.vlan_id}
                />
                {editingPort.vlan_id && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {t('network.ipMustBeInRange')}
                  </p>
                )}
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={editingPort.is_active}
                  onChange={(e) => setEditingPort({...editingPort, is_active: e.target.checked})}
                  className="rounded border-gray-300 dark:border-gray-600 text-primary shadow-sm focus:border-primary/50 focus:ring focus:ring-primary/50 focus:ring-opacity-50"
                />
                <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">{t('network.portActiveLabel')}</span>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setEditingPort(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('network.cancel')}
              </button>
              <button
                onClick={() => handlePortSave(editingPort.id, {
                  port_name: editingPort.port_name,
                  connected_device: editingPort.connected_device,
                  description: editingPort.description,
                  is_active: editingPort.is_active,
                  vlan_id: editingPort.vlan_id,
                  ip_address: editingPort.ip_address
                })}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {t('network.save')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* VLAN Management Modal */}
      <Modal isOpen={showVlanModal} onClose={() => setShowVlanModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.vlanMgmtTitle')}
                </h2>
                <button
                  onClick={() => {
                    setShowVlanModal(false);
                    setEditingVlan(null);
                    setVlanData({ vlan_id: '', vlan_name: '', description: '' });
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                <div className="flex items-center space-x-3">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {language === 'en' ? `Total ${vlansData.length} VLANs defined` : `Toplam ${vlansData.length} VLAN tanımlı`}
                  </p>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder={t('network.searchVlan')}
                      value={vlanSearchTerm}
                      onChange={(e) => setVlanSearchTerm(e.target.value)}
                      className="pl-8 pr-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={exportVLANs}
                    className="flex items-center px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
                  >
                    <Archive className="w-4 h-4 mr-1" />
                    {t('network.export')}
                  </button>
                  <button
                    onClick={() => {
                      setEditingVlan(null);
                      setVlanData({ vlan_id: '', vlan_name: '', description: '', network_address: '', subnet_mask: '', gateway: '', ip_range: '', location_id: 0 });
                    }}
                    className="flex items-center px-3 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    {t('network.newVlan')}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    {editingVlan ? t('network.editVlanTitle') : t('network.addVlanTitle')}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.vlanIdLabel')}
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="4094"
                        value={vlanData.vlan_id}
                        onChange={(e) => setVlanData({...vlanData, vlan_id: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="10"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.vlanNameLabel')}
                      </label>
                      <input
                        type="text"
                        value={vlanData.vlan_name}
                        onChange={(e) => setVlanData({...vlanData, vlan_name: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Management"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.labelLocation')}
                      </label>
                      <select
                        value={vlanData.location_id}
                        onChange={(e) => setVlanData({...vlanData, location_id: parseInt(e.target.value)})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value={0}>{t('network.selectCabinet')}</option>
                        {locationsData.map((location) => (
                          <option key={location.id} value={location.id}>
                            {location.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.networkAddressLabel')}
                      </label>
                      <input
                        type="text"
                        value={vlanData.network_address}
                        onChange={(e) => {
                          const value = e.target.value;
                          setVlanData({...vlanData, network_address: value});
                          calculateNetworkInfo(value);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="192.168.10.0/24"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.subnetMaskLabel')}
                      </label>
                      <input
                        type="text"
                        value={vlanData.subnet_mask}
                        onChange={(e) => setVlanData({...vlanData, subnet_mask: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="255.255.255.0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.gatewayLabel')}
                      </label>
                      <input
                        type="text"
                        value={vlanData.gateway}
                        onChange={(e) => setVlanData({...vlanData, gateway: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="192.168.10.1"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.ipRangeLabel')}
                      </label>
                      <input
                        type="text"
                        value={vlanData.ip_range}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-gray-50 dark:bg-gray-700 dark:bg-gray-600 text-gray-700 dark:text-gray-300"
                        placeholder={t('network.autoCalculated')}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.labelDescription')}
                      </label>
                      <textarea
                        value={vlanData.description}
                        onChange={(e) => setVlanData({...vlanData, description: e.target.value})}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="VLAN hakkında açıklama..."
                      />
                    </div>
                    <button
                      onClick={handleVlanSave}
                      className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                    >
                      {editingVlan ? t('network.update') : t('network.save')}
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    {t('network.existingVlans')}
                  </h3>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {vlansData.filter(vlan =>
                      String(vlan.vlan_id || '').toString().includes(vlanSearchTerm) ||
                      String(vlan.vlan_name || '').toLowerCase().includes(vlanSearchTerm.toLowerCase()) ||
                      (vlan.location_name && String(vlan.location_name).toLowerCase().includes(vlanSearchTerm.toLowerCase()))
                    ).map((vlan) => (
                      <div
                        key={vlan.id}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                      >
                        <div>
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            VLAN {vlan.vlan_id} - {vlan.vlan_name}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {t('network.locationVlanLabel')} {vlan.location_name || t('network.notSpecified')}
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={() => {
                              setSelectedVlanForDetail(vlan);
                              setShowVlanDetailModal(true);
                            }}
                            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                            title="Detayları Gör"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingVlan(vlan);
                              setVlanData({
                                vlan_id: vlan.vlan_id.toString(),
                                vlan_name: vlan.vlan_name,
                                description: vlan.description || '',
                                network_address: vlan.network_address || '',
                                subnet_mask: vlan.subnet_mask || '',
                                gateway: vlan.gateway || '',
                                ip_range: vlan.ip_range || '',
                                location_id: vlan.location_id || 0
                              });
                            }}
                            className="p-1 text-gray-400 hover:text-primary transition-colors"
                            title="Düzenle"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleVlanDelete(vlan.id)}
                            className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                            title="Sil"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>

      {/* Empty Ports Modal */}
      <Modal isOpen={showEmptyPortsModal} onClose={() => setShowEmptyPortsModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {language === 'en' ? `Empty Ports (${emptyPorts.length})` : `Boş Portlar (${emptyPorts.length} adet)`}
                </h2>
                <button
                  onClick={() => setShowEmptyPortsModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {emptyPorts.map((port) => (
                  <div
                    key={port.id}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600"
                  >
                    <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                      Port {port.port_number}
                    </div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {port.port_name || t('network.noPortName')}
                    </div>
                  </div>
                ))}
              </div>
              
              {emptyPorts.length === 0 && (
                <div className="text-center py-8">
                  <p className="text-gray-500 dark:text-gray-400">{t('network.allPortsFull')}</p>
                </div>
              )}
            </div>
          </div>
        </Modal>

      {/* Backup Management Modal */}
      <Modal isOpen={showBackupModal} onClose={() => setShowBackupModal(false)}>
          {selectedSwitch && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[85vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.backupMgmtTitle')} - {selectedSwitch.switch_name}
                </h2>
                <button
                  onClick={() => {
                    setShowBackupModal(false);
                    setBackupData({ backup_type: 'text', config_text: '', description: '' });
                    setBackupFile(null);
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    {t('network.newBackupTitle')}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {t('network.backupTypeLabel')}
                      </label>
                      <div className="flex space-x-4">
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="text"
                            checked={backupData.backup_type === 'text'}
                            onChange={(e) => setBackupData({...backupData, backup_type: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('network.backupTypeText')}</span>
                        </label>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            value="file"
                            checked={backupData.backup_type === 'file'}
                            onChange={(e) => setBackupData({...backupData, backup_type: e.target.value})}
                            className="mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('network.backupTypeFile')}</span>
                        </label>
                      </div>
                    </div>

                    {backupData.backup_type === 'text' ? (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('network.configTextLabel')}
                        </label>
                        <textarea
                          value={backupData.config_text}
                          onChange={(e) => {
                            setBackupData({...backupData, config_text: e.target.value});
                            triggerConfigAnalysis(e.target.value);
                          }}
                          rows={8}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                          placeholder="Switch konfigürasyon metni..."
                        />
                        {/* Config Analysis Results */}
                        {analyzingConfig && (
                          <div className="mt-2 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                            <div className="animate-spin w-3 h-3 border-2 border-orange-500 border-t-transparent rounded-full" />
                            {t('network.analyzingConfig')}
                          </div>
                        )}
                        {configAnalysis && !analyzingConfig && (
                          <div className="mt-3 space-y-2" data-config-analysis>
                            {/* Summary badges */}
                            <div className="flex flex-wrap gap-2 text-xs font-medium">
                              {configAnalysis.summary.critical > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />
                                  {configAnalysis.summary.critical} {language === 'en' ? 'Critical' : 'Kritik'}
                                </span>
                              )}
                              {configAnalysis.summary.warning > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" />
                                  {configAnalysis.summary.warning} {language === 'en' ? 'Warning' : 'Uyarı'}
                                </span>
                              )}
                              {configAnalysis.summary.info > 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 inline-block" />
                                  {configAnalysis.summary.info} {language === 'en' ? 'Info' : 'Bilgi'}
                                </span>
                              )}
                              {configAnalysis.summary.total === 0 && (
                                <span className="flex items-center gap-1 px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                                  {t('network.noIssues')}
                                </span>
                              )}
                            </div>
                            {/* Finding cards */}
                            <div className="max-h-52 overflow-y-auto space-y-1.5 pr-1">
                              {configAnalysis.findings.map((f: any, i: number) => (
                                <div key={i} className={`rounded-md p-2.5 text-xs border-l-4 ${
                                  f.level === 'critical'
                                    ? 'bg-red-50 dark:bg-red-900/20 border-red-500 text-red-800 dark:text-red-300'
                                    : f.level === 'warning'
                                    ? 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-500 text-yellow-800 dark:text-yellow-300'
                                    : 'bg-blue-50 dark:bg-blue-900/20 border-blue-500 text-blue-800 dark:text-blue-300'
                                }`}>
                                  <div className="font-semibold mb-0.5">{f.level === 'critical' ? '🔴' : f.level === 'warning' ? '🟡' : '🔵'} {f.title}</div>
                                  <div className="opacity-90">{f.message}</div>
                                  <div className="mt-1 opacity-75 italic">💡 {f.suggestion}</div>
                                  {f.lines && f.lines.length > 0 && (
                                    <div className="mt-1 font-mono bg-black/10 dark:bg-black/20 rounded px-1.5 py-0.5 text-xs">
                                      {f.lines.join(' | ')}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {t('network.backupFileLabel')}
                        </label>
                        <input
                          type="file"
                          accept=".txt,.cfg,.conf,.backup"
                          onChange={(e) => setBackupFile(e.target.files?.[0] || null)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('network.backupDescLabel')}
                      </label>
                      <textarea
                        value={backupData.description}
                        onChange={(e) => setBackupData({...backupData, description: e.target.value})}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        placeholder="Backup hakkında açıklama..."
                      />
                    </div>

                    <button
                      onClick={saveBackup}
                      className="w-full px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
                    >
                      {t('network.backupUploadBtn')}
                    </button>
                  </div>
                </div>

                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">
                    {t('network.backupHistoryTitle')}
                  </h3>
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {backupHistory.map((backup) => (
                      <div
                        key={backup.id}
                        className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 dark:border-gray-600"
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900 dark:text-gray-100">
                            {backup.backup_type === 'text' ? t('network.textBackup') : t('network.fileBackup')}
                          </div>
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => {
                                if (backup.backup_type === 'text') {
                                  const blob = new Blob([backup.config_text], { type: 'text/plain' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${selectedSwitch?.switch_name}-backup-${new Date(backup.created_at).toISOString().split('T')[0]}.txt`;
                                  a.click();
                                  URL.revokeObjectURL(url);
                                } else {
                                  window.open(`${import.meta.env.VITE_API_URL?.replace('/api', '') || 'http://localhost:3004'}${backup.file_path}`, '_blank');
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-primary transition-colors"
                              title="İndir"
                            >
                              <Archive className="w-3 h-3" />
                            </button>
                            {backup.backup_type === 'text' && (
                              <button
                                onClick={async () => {
                                  setConfigAnalysis(null);
                                  setAnalyzingConfig(true);
                                  try {
                                    const result = await apiService.analyzeNetworkSwitchConfig(backup.config_text);
                                    setConfigAnalysis(result);
                                    setBackupData(prev => ({ ...prev, config_text: backup.config_text, backup_type: 'text' }));
                                    // Scroll to analysis
                                    setTimeout(() => {
                                      document.querySelector('[data-config-analysis]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                                    }, 100);
                                  } catch (err) {
                                    showGlobalNotification.error(t('notif.filter.error'), t('network.errAnalysisError'));
                                  } finally {
                                    setAnalyzingConfig(false);
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-orange-600 transition-colors"
                                title="Konfigürasyonu Analiz Et"
                              >
                                <Shield className="w-3 h-3" />
                              </button>
                            )}
                            {backup.backup_type === 'text' && (
                              <button
                                onClick={() => {
                                  alert(backup.config_text);
                                }}
                                className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                                title="Görüntüle"
                              >
                                <Eye className="w-3 h-3" />
                              </button>
                            )}
                            <button
                              onClick={async () => {
                                if (confirm(t('network.confirmDeleteBackup'))) {
                                  try {
                                    await apiService.deleteSwitchBackup(backup.id);
                                    await loadBackupHistory(selectedSwitch!.id);
                                    showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('network.successBackupDeleted'));
                                  } catch (error: any) {
                                    showGlobalNotification.error(t('notif.filter.error'), t('network.errDeletingBackup'));
                                  }
                                }
                              }}
                              className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                              title="Sil"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {new Date(backup.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          {t('network.uploadedByLabel')} {backup.created_by_name}
                        </div>
                        {backup.description && (
                          <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {backup.description}
                          </div>
                        )}
                      </div>
                    ))}
                    
                    {backupHistory.length === 0 && (
                      <div className="text-center py-8">
                        <p className="text-gray-500 dark:text-gray-400">{t('network.noBackups')}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </Modal>

      {/* VLAN Detail Modal */}
      <Modal isOpen={showVlanDetailModal} onClose={() => setShowVlanDetailModal(false)}>
          {selectedVlanForDetail && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.vlanDetailTitle')}
                </h2>
                <button
                  onClick={() => setShowVlanDetailModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.vlanIdLabel')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.vlan_id}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.vlanNameLabel')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.vlan_name}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.labelLocation')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.location_name || t('network.notSpecified')}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.networkAddressLabel')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.network_address || t('network.notSpecified')}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.subnetMaskLabel')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.subnet_mask || t('network.notSpecified')}
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('network.gatewayLabel')}
                    </label>
                    <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                      {selectedVlanForDetail.gateway || t('network.notSpecified')}
                    </div>
                  </div>
                </div>
              </div>
              
              {selectedVlanForDetail.ip_range && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.ipRangeLabel')}
                  </label>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                    {selectedVlanForDetail.ip_range}
                  </div>
                </div>
              )}
              
              {selectedVlanForDetail.description && (
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('network.labelDescription')}
                  </label>
                  <div className="px-3 py-2 bg-gray-50 dark:bg-gray-700 rounded-md text-gray-900 dark:text-gray-100">
                    {selectedVlanForDetail.description}
                  </div>
                </div>
              )}
            </div>
          </div>
          )}
        </Modal>

      {/* Maintenance Modal */}
      <Modal isOpen={showMaintenanceModal} onClose={() => setShowMaintenanceModal(false)}>
          {selectedSwitchForMaintenance && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.maintenanceTitle')} - {selectedSwitchForMaintenance.switch_name}
                </h2>
                <button
                  onClick={() => setShowMaintenanceModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.maintenanceTypeLabel')}
                </label>
                <select
                  value={maintenanceData.maintenance_type}
                  onChange={(e) => setMaintenanceData({...maintenanceData, maintenance_type: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="cleaning">{t('network.cleaningType')}</option>
                  <option value="inspection">{t('network.inspectionType')}</option>
                  <option value="backup">{t('network.backupUpdateType')}</option>
                  <option value="firmware_update">{t('network.firmwareUpdateType')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.maintenanceDateLabel')}
                </label>
                <input
                  type="date"
                  value={maintenanceData.maintenance_date}
                  onChange={(e) => setMaintenanceData({...maintenanceData, maintenance_date: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('network.maintenanceNotesLabel')}
                </label>
                <textarea
                  value={maintenanceData.notes}
                  onChange={(e) => setMaintenanceData({...maintenanceData, notes: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="Bakım ile ilgili notlar..."
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowMaintenanceModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('network.cancel')}
              </button>
              <button
                onClick={saveMaintenance}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                {t('network.save')}
              </button>
            </div>
          </div>
          )}
        </Modal>

      {/* Switch Label Print Modal */}
      <Modal isOpen={showLabelModal} onClose={() => setShowLabelModal(false)}>
          {selectedSwitchForLabel && (
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('network.labelPrintTitle')} - {selectedSwitchForLabel.switch_name}
                </h2>
                <button
                  onClick={() => setShowLabelModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-full hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center mb-6">
                <Printer className="w-12 h-12 text-purple-600 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  {t('network.labelPrintDesc')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('network.labelCustomDesc')}
                </p>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('network.labelOrientationLabel')}
                </label>
                <div className="flex space-x-4 justify-center">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="horizontal"
                      checked={labelSettings.orientation === 'horizontal'}
                      onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('network.horizontal')}</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="vertical"
                      checked={labelSettings.orientation === 'vertical'}
                      onChange={(e) => setLabelSettings({...labelSettings, orientation: e.target.value})}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('network.vertical')}</span>
                  </label>
                </div>
              </div>
              
              <div className="bg-primary/10 dark:bg-primary/20 border border-primary/30 dark:border-primary-dark rounded-lg p-4 mb-6">
                <div className="flex items-center mb-2">
                  <Network className="w-4 h-4 text-primary mr-2" />
                  <span className="text-sm font-medium text-primary-dark dark:text-accent">
                    {t('network.switchInfoLabelTitle')}
                  </span>
                </div>
                <div className="text-xs text-primary-dark dark:text-accent space-y-1">
                  <div>IP: {selectedSwitchForLabel.ip_address}</div>
                  <div>Model: {selectedSwitchForLabel.brand} {selectedSwitchForLabel.model}</div>
                  <div>Port: {selectedSwitchForLabel.port_count} {language === 'en' ? 'pcs' : 'adet'}</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('network.labelSizeTitle')}
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        {t('network.labelWidthMm')}
                      </label>
                      <input
                        type="number"
                        min="50"
                        max="200"
                        value={labelSettings.width}
                        onChange={(e) => setLabelSettings({...labelSettings, width: parseInt(e.target.value) || 100})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                        {t('network.labelHeightMm')}
                      </label>
                      <input
                        type="number"
                        min="30"
                        max="100"
                        value={labelSettings.height}
                        onChange={(e) => setLabelSettings({...labelSettings, height: parseInt(e.target.value) || 50})}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 bg-white dark:bg-gray-800 dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => setLabelSettings({width: 100, height: 50})}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-600 transition-colors"
                    >
                      100x50mm
                    </button>
                    <button
                      onClick={() => setLabelSettings({width: 90, height: 50})}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-600 transition-colors"
                    >
                      90x50mm
                    </button>
                    <button
                      onClick={() => setLabelSettings({width: 80, height: 40})}
                      className="px-3 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-200 dark:bg-gray-600 dark:hover:bg-gray-600 transition-colors"
                    >
                      80x40mm
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex flex-col space-y-3">
              <button
                onClick={printSwitchLabel}
                className="w-full px-4 py-3 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors flex items-center justify-center"
              >
                <Printer className="w-4 h-4 mr-2" />
                {t('network.printLabelBtn')} ({labelSettings.width}x{labelSettings.height}mm)
              </button>
              <button
                onClick={() => setShowLabelModal(false)}
                className="w-full px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:bg-gray-700 dark:hover:bg-gray-700 transition-colors"
              >
                {t('network.cancel')}
              </button>
            </div>
          </div>
          )}
        </Modal>
    </div>
  );
};

export default NetworkSwitchesPage;